import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { MarkdownPreview } from '@/components/admin/docs/MarkdownPreview';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Pencil, Trash2, GripVertical, FileText } from 'lucide-react';
import { ICON_OPTIONS, getIconComponent } from '@/lib/admin/icons';
import { DATA_TYPE_DOC_CATEGORIES } from '@/lib/admin/constants';
import {
  AdminPageHeader,
  AdminSheet,
  AdminEmptyState,
  AdminTableSkeleton,
  DeleteConfirmDialog,
} from '@/components/admin/shared';
import { useAdminCrud } from '@/hooks/useAdminCrud';

interface DataTypeDoc {
  id: string;
  slug: string;
  name: string;
  category: string;
  icon: string;
  short_description: string | null;
  content: string;
  example_value: string | null;
  regex_pattern: string | null;
  display_order: number | null;
  is_active: boolean | null;
  created_at: string;
  updated_at: string;
}

interface FormData {
  slug: string;
  name: string;
  category: string;
  icon: string;
  short_description: string;
  content: string;
  example_value: string;
  regex_pattern: string;
  display_order: number;
  is_active: boolean;
}

const emptyFormData: FormData = {
  slug: '',
  name: '',
  category: 'format',
  icon: 'FileText',
  short_description: '',
  content: '',
  example_value: '',
  regex_pattern: '',
  display_order: 0,
  is_active: true,
};

export function DataTypeDocsManager() {
  const {
    items: docs,
    loading: isLoading,
    editorOpen: sheetOpen,
    setEditorOpen: setSheetOpen,
    editingItem: selectedDoc,
    formData,
    setFormData,
    isSaving,
    deleteDialogOpen,
    setDeleteDialogOpen,
    itemToDelete,
    isDeleting,
    openCreateEditor: handleCreate,
    openEditEditor: handleEdit,
    handleSave,
    confirmDelete: handleDelete,
    handleDelete: confirmDeleteAction,
  } = useAdminCrud<DataTypeDoc, FormData>({
    table: 'data_type_docs',
    select: '*',
    orderBy: { column: 'display_order' },
    entityName: 'data type doc',
    getInitialFormData: () => emptyFormData,
    mapItemToForm: (doc) => ({
      slug: doc.slug,
      name: doc.name,
      category: doc.category,
      icon: doc.icon,
      short_description: doc.short_description || '',
      content: doc.content,
      example_value: doc.example_value || '',
      regex_pattern: doc.regex_pattern || '',
      display_order: doc.display_order || 0,
      is_active: doc.is_active ?? true,
    }),
    buildPayload: (form) => ({
      slug: form.slug,
      name: form.name,
      category: form.category,
      icon: form.icon,
      short_description: form.short_description || null,
      content: form.content,
      example_value: form.example_value || null,
      regex_pattern: form.regex_pattern || null,
      display_order: form.display_order,
      is_active: form.is_active,
    }),
    validatePayload: (form) => {
      if (!form.slug || !form.name) return 'Slug and name are required';
      return null;
    },
  });

  const renderIcon = (iconName: string) => {
    const IconComponent = getIconComponent(iconName);
    return <IconComponent className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <AdminPageHeader
          title="Data Type Docs"
          count={0}
          onAdd={handleCreate}
          addLabel="Add Data Type"
        />
        <AdminTableSkeleton columns={5} rows={5} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Data Type Docs"
        count={docs?.length}
        onAdd={handleCreate}
        addLabel="Add Data Type"
      />

      {!docs || docs.length === 0 ? (
        <AdminEmptyState
          icon={FileText}
          title="No data type docs"
          description="Get started by creating your first data type documentation."
          actionLabel="Add Data Type"
          onAction={handleCreate}
        />
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((doc) => (
                <TableRow 
                  key={doc.id}
                  className="cursor-pointer"
                  onClick={() => handleEdit(doc)}
                >
                  <TableCell>
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {renderIcon(doc.icon)}
                      <span className="font-medium">{doc.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">
                    {doc.slug}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {doc.category}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={doc.is_active ? 'default' : 'secondary'}>
                      {doc.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div 
                      className="flex items-center justify-end gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(doc)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(doc)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit/Create Sheet */}
      <AdminSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title={selectedDoc ? 'Edit Data Type Doc' : 'Create Data Type Doc'}
        description="Configure the data type documentation"
        onSave={handleSave}
        isSaving={isSaving}
        size="xl"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Date Format"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="date"
                className="font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATA_TYPE_DOC_CATEGORIES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="icon">Icon</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <opt.icon className="h-4 w-4" />
                        <span>{opt.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="short_description">Short Description</Label>
            <Input
              id="short_description"
              value={formData.short_description}
              onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
              placeholder="ISO 8601 date format (YYYY-MM-DD)"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="example_value">Example Value</Label>
              <Input
                id="example_value"
                value={formData.example_value}
                onChange={(e) => setFormData({ ...formData, example_value: e.target.value })}
                placeholder="2024-01-15"
                className="font-mono"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="regex_pattern">Regex Pattern</Label>
              <Input
                id="regex_pattern"
                value={formData.regex_pattern}
                onChange={(e) => setFormData({ ...formData, regex_pattern: e.target.value })}
                placeholder="^\d{4}-\d{2}-\d{2}$"
                className="font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Content (Markdown)</Label>
            <MarkdownPreview
              value={formData.content}
              onChange={(value) => setFormData({ ...formData, content: value })}
              placeholder="Write detailed documentation here..."
              minHeight="300px"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="is_active">Active</Label>
              <p className="text-xs text-muted-foreground">Show this documentation publicly</p>
            </div>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>
        </div>
      </AdminSheet>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDeleteAction}
        itemName={itemToDelete?.name}
        itemType="data type doc"
        isDeleting={isDeleting}
      />
    </div>
  );
}